import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import CloseIcon from "@mui/icons-material/Close";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

// What the float means for the screen the cashier is on.
const DISCLAIMER = {
  issue:    "This player still owes a float. Chips issued now come from the wallet as usual — the float stays pending until it is returned or settled.",
  cashout:  "By default the float is recovered FIRST from this cash-out, then any credit (loan), and only the remainder goes to the wallet. Turn off \"Recover float\" on the form to skip it.",
  deposit:  "By default cash / bank / other deposits repay the float FIRST, then any credit (loan), and only the remainder is added to the wallet. Turn off \"Repay float\" on the form to skip it.",
  withdraw: "This player owes a float. Recover the float (return chips or settle from Float Exchange) before paying out a withdrawal.",
};

// Float warning shown once per player when a player with an outstanding float
// is loaded. Pass `user` only while the parent screen is open; it re-arms when
// user goes null. `big` makes it near full-screen (chip cash-out), otherwise
// it takes half the screen.
export default function FloatPopup({ user, context = "issue", big = false, disabled = false }) {
  const [dismissedId, setDismissedId] = useState(null);
  const [floats, setFloats] = useState([]);
  const token = useSelector((state) => state.loginSlice.token);
  const id = user?._id ? String(user._id) : null;
  const due = Number(user?.float_outstanding || 0);

  useEffect(() => { if (!id) setDismissedId(null); }, [id]);

  const open = !disabled && due > 0 && !!id && dismissedId !== id;
  const close = () => setDismissedId(id);

  useEffect(() => {
    if (!open || !user?.playerId) { setFloats([]); return; }
    axiosRequest("GET", `${apiBaseUrl}/float-exchange?status=OPEN&player_id=${encodeURIComponent(user.playerId)}&size=20`, token)
      .then((res) => setFloats((res?.data?.floatList || []).filter((f) => String(f.user_id) === id)))
      .catch(() => setFloats([]));
  }, [open, id, user?.playerId, token]);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== "backdropClick" && reason !== "escapeKeyDown") close(); }}
      maxWidth={false}
      sx={{ zIndex: 1900 }}
      PaperProps={{
        sx: {
          width: big ? { xs: "100%", md: "85vw" } : { xs: "100%", md: "50vw" },
          minHeight: big ? "85vh" : "50vh",
          m: { xs: 1, md: 4 },
          background: "linear-gradient(135deg,#e65100,#f57c00 55%,#ffa726)",
          color: "#fff",
          borderRadius: 3,
        },
      }}
    >
      <IconButton onClick={close} sx={{ position: "absolute", top: 12, right: 12, color: "#fff" }} aria-label="Close">
        <CloseIcon fontSize="large" />
      </IconButton>

      <Box
        display="flex" flexDirection="column" alignItems="center" justifyContent="center"
        textAlign="center" flex={1} px={3} py={5} gap={big ? 3 : 2}
      >
        <ReportProblemIcon sx={{ fontSize: big ? { xs: 80, md: 120 } : { xs: 60, md: 80 } }} />
        <Typography fontWeight={900} letterSpacing={3} sx={{ fontSize: big ? { xs: 34, md: 56 } : { xs: 28, md: 40 } }}>
          FLOAT OUTSTANDING
        </Typography>

        <Box>
          <Typography variant="h6" fontWeight={700}>{user?.name || "Player"}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {[user?.playerId && `ID: ${user.playerId}`, user?.phone && `📱 ${user.phone}`].filter(Boolean).join("  |  ")}
          </Typography>
        </Box>

        <Typography fontWeight={900} sx={{ fontSize: big ? { xs: 48, md: 80 } : { xs: 40, md: 56 }, lineHeight: 1 }}>
          ₹{fmt(due)}
        </Typography>

        {floats.length > 0 && (
          <Box sx={{ width: "100%", maxWidth: 560 }}>
            {floats.map((f) => (
              <Box key={f._id} display="flex" justifyContent="space-between" sx={{ py: 0.5, borderBottom: "1px solid rgba(255,255,255,0.3)" }}>
                <Typography variant="body2">
                  {new Date(f.date || f.createdAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  {f.table_name ? ` · ${f.table_name}` : ""}
                  {f.reference_name ? ` · Ref: ${f.reference_name}` : ""}
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  ₹{fmt(f.outstanding)} <span style={{ opacity: 0.8, fontWeight: 400 }}>of ₹{fmt(f.total_amount)}</span>
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ background: "rgba(0,0,0,0.22)", border: "2px solid rgba(255,255,255,0.55)", borderRadius: 3, px: 3, py: 2, maxWidth: 720, width: "100%" }}>
          <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 2 }}>⚠️ Disclaimer</Typography>
          <Typography variant={big ? "h6" : "body1"} fontWeight={600}>
            {DISCLAIMER[context] || DISCLAIMER.issue}
          </Typography>
        </Box>

        <Button
          variant="contained" size="large" onClick={close} autoFocus
          sx={{ mt: 1, px: 5, py: 1.2, fontWeight: 700, fontSize: 16, background: "#fff", color: "#e65100", "&:hover": { background: "#fff3e0" } }}
        >
          I Understand — Close
        </Button>
      </Box>
    </Dialog>
  );
}
